import React, { useEffect, useRef, useState } from 'react';
import {
  ToUserTypings,
  TypeOfUser,
  getNotificationsCount,
  getTypeOfUser,
  notificationsCount,
  profileImage,
  showCriticalMissedNotification,
} from '@pharmaplan/common';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useIdleTimer } from 'react-idle-timer';

import { getMultipleEventAdd } from '../../selectors/calendarSelector';
import { Constants, OutcomeModalTypes } from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import strings from '../../localization';
import {
  setTempEvents,
  setTempVacations,
} from '../../reducers/mainCalendarReducer';

import useEnforceUpdate from '../useEnforceUpdate';
import useAdmin from '../useAdmin';
import useUser from '../useUser';
import { logoutHandler } from '../../helpers/Functions';
import { setDialog } from '../../reducers/dialogReducer';
import OutcomeModal from '../../components/Modals/OutcomeModal';
import useShowCriticialNotification from '../PushNotifications/useShowCriticalNotification';

const { pharmacist, pharmacy } = TypeOfUser;
const { sessionTimeOut } = OutcomeModalTypes;

const useNavbar = () => {
  const navigate = useNavigate();
  const ref = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAdmin } = useAdmin();

  const { name = '' } = useUser();

  const [notifications, setNotifications] = useState<HTMLButtonElement | null>(
    null,
  );

  const userType = useAppSelector(getTypeOfUser);
  const multipleEventAdd = useAppSelector(getMultipleEventAdd);
  const avatar = useAppSelector(profileImage);
  const notificationCount = useAppSelector(notificationsCount);

  const { pathname } = location ?? {};

  const isPharmacist = userType === TypeOfUser.pharmacist;
  const isReports = pathname === Constants.paths.reports;
  const isNotificationOpen = !!notifications;

  const userConfig = ToUserTypings({
    [pharmacist]: {
      buttonLabel: strings.availabilities,
    },
    [pharmacy]: {
      buttonLabel: strings.workshifts,
    },
  });

  // SESSION TIMEOUT
  const handleIdle = () => {
    logoutHandler();
    dispatch(
      setDialog({
        Component: (
          <OutcomeModal
            type={sessionTimeOut}
            message={strings.yourSessionHasTimedOut}
          />
        ),
        showCloseButton: true,
        heading: { title: '' },
      }),
    );
  };

  const { REACT_APP_SESSION_TIMEOUT, REACT_APP_ADMIN_TIMEOUT } = process.env ?? {};

  useIdleTimer({
    onIdle: handleIdle,
    crossTab: true,
    syncTimers: 200,
    timeout:
      parseInt(
        (isAdmin ? REACT_APP_ADMIN_TIMEOUT : REACT_APP_SESSION_TIMEOUT) ?? '0',
        10,
      )
      * 60
      * 1000,
  });

  const handleNotificationClick = (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | { currentTarget: HTMLButtonElement },
  ) => {
    dispatch(showCriticalMissedNotification(false));
    toast.dismiss();
    setNotifications(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotifications(null);
  };

  useEnforceUpdate();
  useShowCriticialNotification(() => {
    if (ref.current) {
      handleNotificationClick({ currentTarget: ref.current });
    }
  });

  useEffect(() => {
    if (multipleEventAdd) {
      dispatch(setTempEvents([]));
      dispatch(setTempVacations([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multipleEventAdd]);

  useEffect(() => {
    if (!isAdmin) {
      dispatch(getNotificationsCount());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isNotificationOpen]);

  return {
    name,
    userType,
    avatar,
    notificationCount,
    userConfig,
    isPharmacist,
    isReports,
    isNotificationOpen,
    notifications,
    pathname,
    navigate,
    dispatch,
    handleNotificationClick,
    handleNotificationClose,
    ref,
  };
};

export default useNavbar;
